/* The shared stage for the three.js labs: renderer, bloom, lights, the grid floor, and the clock.

   A lab builds its scene on the stage and hands run() one function, frameAt(t), that draws the
   frame at t seconds. Nothing moves on its own, so a lab is two things at once:
     - live, in a browser: t is the wall clock, and the reader drags and clicks;
     - recorded, by brand/render.py: t steps frame by frame, the same every time, and the page
       becomes a shot in an episode ({html: ...} in episode.yaml).
   Randomness comes from rng(seed), never Math.random, for the same reason. */
import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

declare global {
  interface Window {
    frameAt?: (t: number) => void;
    DURATION?: number;
    STILL_AT?: number;
  }
}

// brand/tokens.css, as three.js colours
export const C = {
  bg: 0x05070d, panel: 0x0b1120, ink: 0xe6edf3, muted: 0x8b98ad,
  see: 0x34d399, think: 0xa78bfa, act: 0x38bdf8, amber: 0xfbbf24, grid: 0x1a6b80,
  steel: 0x6b788c, shell: 0x3a4456,
} as const;

export const q = new URLSearchParams(location.search);
// recorded: driven by the renderer (or ?demo): the scripted take, no controls
export const recorded = (navigator.webdriver && !q.has("live")) || q.has("demo");

export function rng(seed = 1): () => number {  // mulberry32
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const clamp = (x: number, a: number, b: number): number => Math.min(b, Math.max(a, x));
export const span = (t: number, a: number, b: number): number => clamp((t - a) / (b - a), 0, 1);
export const ease = (x: number): number => x * x * (3 - 2 * x);

export interface Stage {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  composer: EffectComposer;
  bloom: UnrealBloomPass;
  resize(): void;
  render(): void;
}

export function createStage(canvas: HTMLCanvasElement): Stage {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: recorded });
  renderer.setPixelRatio(recorded ? 1 : Math.min(devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = .9;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(C.bg);
  scene.fog = new THREE.Fog(C.bg, 2.6, 6);
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), .04).texture;
  scene.environmentIntensity = .16;

  const FOV = 34;  // vertical, at 16:9
  const camera = new THREE.PerspectiveCamera(FOV, 16 / 9, .02, 30);

  // the same three lights as the MuJoCo shots: a white key with shadows, a teal rim, a violet fill
  const key = new THREE.DirectionalLight(0xe8ecf5, 1.5);
  key.position.set(.6, 3, 1.2);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  Object.assign(key.shadow.camera, { left: -1.5, right: 1.5, top: 1.5, bottom: -1.5, near: .5, far: 6 });
  key.shadow.bias = -.0002;
  key.shadow.normalBias = .015;
  key.shadow.radius = 3;
  const rim = new THREE.DirectionalLight(0x4fb3c8, 1.1);
  rim.position.set(-1.6, 1.2, -1.4);
  const fill = new THREE.DirectionalLight(0x8a6fd6, .5);
  fill.position.set(-1.2, 1.4, 1.6);
  scene.add(key, rim, fill, new THREE.AmbientLight(0x1a2033, .35));

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(16, 9), .6, .35, .9);
  composer.addPass(bloom);
  composer.addPass(new OutputPass());

  function resize(): void {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(w, h);
    camera.aspect = w / h;
    // narrower than 16:9 (a laptop window, a phone upright): keep the width in frame, not the height
    const half = Math.tan(FOV / 2 * Math.PI / 180) * 16 / 9 / Math.min(camera.aspect, 16 / 9);
    camera.fov = Math.min(80, 2 * Math.atan(half) * 180 / Math.PI);
    camera.updateProjectionMatrix();
  }
  addEventListener("resize", resize);
  resize();

  return { renderer, scene, camera, composer, bloom, resize, render: () => composer.render() };
}

/* A dark floor that takes shadows, and over it a grid that glows faintly and fades with distance:
   minor lines every 10 cm, major every 50 cm. */
export function floor(scene: THREE.Scene, size = 8): { base: THREE.Mesh; grid: THREE.Mesh } {
  const base = new THREE.Mesh(new THREE.PlaneGeometry(size, size),
    new THREE.MeshStandardMaterial({ color: 0x070a12, roughness: .85, metalness: 0 }));
  base.rotation.x = -Math.PI / 2;
  base.receiveShadow = true;
  const grid = new THREE.Mesh(new THREE.PlaneGeometry(size, size), new THREE.ShaderMaterial({
    transparent: true, depthWrite: false,
    uniforms: { colour: { value: new THREE.Color(C.grid) } },
    vertexShader: "varying vec2 p; void main(){ p = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }",
    fragmentShader: `varying vec2 p; uniform vec3 colour;
      float line(vec2 q, float step, float w){ vec2 g = abs(fract(q / step - .5) - .5) * step / fwidth(q); return 1. - min(min(g.x, g.y) / w, 1.); }
      void main(){
        float a = .1 * line(p, .1, 1.) + .3 * line(p, .5, 1.3);
        a *= 1. - smoothstep(.4, 2., length(p));
        gl_FragColor = vec4(colour, a);
      }`,
  }));
  grid.rotation.x = -Math.PI / 2;
  grid.position.y = .0005;
  scene.add(base, grid);
  return { base, grid };
}

export interface Ring extends THREE.Group { material: THREE.LineBasicMaterial }

/* An instrument ring on the floor, like the one round the logo: a thin circle with a tick
   every 10 degrees and a longer one every 30. */
export function ring(radius: number, colour: number = C.act): Ring {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i < 36; i++) {
    const a = i / 36 * Math.PI * 2, l = i % 3 ? .018 : .045;
    pts.push(new THREE.Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius),
      new THREE.Vector3(Math.cos(a) * (radius - l), 0, Math.sin(a) * (radius - l)));
  }
  const material = new THREE.LineBasicMaterial({ color: colour, transparent: true, opacity: .55 });
  const group = Object.assign(new THREE.Group(), { material });
  group.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), material));
  const circle = new THREE.EllipseCurve(0, 0, radius, radius).getPoints(180).map(p => new THREE.Vector3(p.x, 0, p.y));
  group.add(new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(circle), material));
  group.position.y = .002;
  return group;
}

/* Hand frameAt to the page, the renderer and the browser. Recorded: window.frameAt for
   brand/render.py, and ?at= for a still. Live: the wall clock drives it (looping over dur in
   ?demo). The lab's own frameAt steps its simulation forward from the last t it saw.
   ?from=s starts the take s seconds in: when an episode shows one lab in several segments, each
   segment is a slice of the same scripted take ({html: ..., from: 9} in episode.yaml). */
export function run(frameAt: (t: number) => void, { dur = 12, still }: { dur?: number; still?: number } = {}): void {
  const from = Number(q.get("from") || 0);
  const at = from ? (t: number): void => frameAt(t + from) : frameAt;
  still = q.has("at") ? Number(q.get("at")) : still ?? dur / 2;   // brand/render.py takes a still at STILL_AT
  window.DURATION = dur;
  window.STILL_AT = still;
  window.frameAt = at;
  document.body.classList.toggle("recorded", recorded);
  if (navigator.webdriver && !q.has("live")) {
    at(still);
    return;
  }
  const t0 = performance.now();
  const loop = q.has("demo");
  const tick = (now: number): void => {
    const t = (now - t0) / 1000;
    at(loop ? t % dur : t);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export interface ScreenPoint { x: number; y: number; front: boolean }

// a point in the scene to CSS pixels on the canvas, for HTML labels that follow objects
export function toScreen(v: THREE.Vector3, camera: THREE.Camera, canvas: HTMLCanvasElement): ScreenPoint {
  const p = v.clone().project(camera);
  return { x: (p.x + 1) / 2 * canvas.clientWidth, y: (1 - p.y) / 2 * canvas.clientHeight, front: p.z < 1 };
}

// an element by id, typed; throws if the page doesn't have it
export function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`#${id} is missing`);
  return e as T;
}
